import { FieldworkAudience } from '../../../data/model/audience'
import {
  AudienceLogicProposition,
  AudienceQuestionLogic,
  DisplayLogicDraft,
  DisplayLogicRow
} from '../../../data/model/audienceQuestionLogic'
import { Operator } from '../../../data/model/common'
import {
  SourceQuestion,
  clauseToRows,
  describeRow,
  getSourceQuestions,
  isRowComplete
} from '../audienceConditions'

// A forward reference would hide the question from everyone, so only earlier ones are offered.
export const sourceQuestionsFor = (
  members: FieldworkAudience[] | undefined,
  gatedQuestionId: string
): SourceQuestion[] => {
  const ordered = getSourceQuestions(members)
  const index = ordered.findIndex((q) => q.questionId === gatedQuestionId)

  return index === -1 ? [] : ordered.slice(0, index)
}

// One clause naming several questions is an OR; anything else reads as an AND.
export const operatorOf = (logic: AudienceQuestionLogic): Operator => {
  const questionsInFirstClause = new Set(
    (logic[0] ?? []).map((p) => p.proposition.questionId)
  )

  return logic.length === 1 && questionsInFirstClause.size > 1
    ? Operator.Or
    : Operator.And
}

export const toDraft = (logic: AudienceQuestionLogic): DisplayLogicDraft => ({
  rows: clauseToRows(logic),
  operator: operatorOf(logic)
})

// Incomplete rows are dropped.
export const buildDisplayLogicCondition = (
  draft: DisplayLogicDraft,
  surveyId: string
): AudienceLogicProposition[][] => {
  const rows = draft.rows.filter(isRowComplete)
  if (rows.length === 0) return []

  const toPropositions = (row: DisplayLogicRow) =>
    row.responseOptionIds.map((responseOptionId) => ({
      proposition: { surveyId, questionId: row.questionId, responseOptionId },
      negated: row.negated
    }))

  return draft.operator === Operator.Or
    ? [rows.flatMap(toPropositions)]
    : rows.map(toPropositions)
}

export const isDraftComplete = (draft: DisplayLogicDraft): boolean =>
  draft.rows.length > 0 && draft.rows.every(isRowComplete)

export const describeDisplayLogic = (
  draft: DisplayLogicDraft,
  questions: SourceQuestion[]
): string =>
  draft.rows
    .map((row) => describeRow(row, questions))
    .join(draft.operator === Operator.Or ? ' OR ' : ' AND ')

export const hasDisplayLogic = (
  logic: AudienceQuestionLogic | undefined
): boolean => (logic ?? []).some((clause) => clause.length > 0)
